import { useEffect, useRef, useState } from 'react';
import { submitAccessCode } from './api';

const CODE_LENGTH = 5;

export default function AccessGate({ onGranted }) {
  const [chars, setChars] = useState(() => Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const inputs = useRef([]);
  const checking = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  function focusAt(index) {
    const input = inputs.current[index];
    if (!mounted.current || !input || input.disabled) return;
    input.focus();
    input.select();
  }

  function writeChars(next, focusIndex, { clearError = true } = {}) {
    if (checking.current) return;
    setChars(next);
    if (clearError) setError('');
    if (next.every(Boolean)) {
      checkCode(next.join(''));
      return;
    }
    if (focusIndex != null) {
      requestAnimationFrame(() => focusAt(focusIndex));
    }
  }

  function fillFrom(start, text) {
    const incoming = text.replace(/\s/g, '').slice(0, CODE_LENGTH - start).split('');
    if (!incoming.length) return;
    const next = [...chars];
    incoming.forEach((char, offset) => {
      next[start + offset] = char;
    });
    const focusIndex = Math.min(start + incoming.length, CODE_LENGTH - 1);
    writeChars(next, incoming.length > 1 || next[focusIndex] ? focusIndex : start + 1);
  }

  function onChange(index, event) {
    const value = event.target.value;
    if (value.length > 1) {
      fillFrom(index, value);
      return;
    }
    const next = [...chars];
    next[index] = value.slice(-1);
    writeChars(next, value && index < CODE_LENGTH - 1 ? index + 1 : null);
  }

  function onKeyDown(index, event) {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const next = [...chars];
      if (next[index]) {
        next[index] = '';
        writeChars(next, null);
        return;
      }
      if (index > 0) {
        next[index - 1] = '';
        writeChars(next, index - 1);
      }
      return;
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusAt(index - 1);
    }
    if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      event.preventDefault();
      focusAt(index + 1);
    }
  }

  function onPaste(event) {
    event.preventDefault();
    const target = inputs.current.indexOf(event.target);
    fillFrom(target >= 0 ? target : 0, event.clipboardData.getData('text'));
  }

  async function checkCode(code) {
    if (checking.current) return;
    checking.current = true;
    setPending(true);
    setError('');
    try {
      await submitAccessCode(code);
      if (!mounted.current) return;
      onGranted();
    } catch (err) {
      if (!mounted.current) return;
      checking.current = false;
      setPending(false);
      setError(err.message || 'Incorrect access code.');
      writeChars(Array(CODE_LENGTH).fill(''), 0, { clearError: false });
    }
  }

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
      <form
        onSubmit={(event) => event.preventDefault()}
        aria-busy={pending}
        className="w-full max-w-md bg-surface border border-edge rounded-2xl shadow-sm p-8"
      >
        <p className="text-xs tracking-[0.18em] uppercase text-ink-subtle">EDVise</p>
        <h1 className="mt-2 text-2xl font-semibold text-ink">HKU Arts Knowledge Base</h1>
        <p className="mt-2 text-sm text-ink-muted">Enter the access code to open this demonstration.</p>
        <fieldset className="mt-6 border-0 p-0">
          <legend className="text-sm font-medium text-ink">Access code</legend>
          <div className="mt-3 flex justify-between gap-2" onPaste={onPaste}>
            {chars.map((char, index) => (
              <input
                key={index}
                ref={(node) => {
                  inputs.current[index] = node;
                }}
                value={char}
                onChange={(event) => onChange(index, event)}
                onKeyDown={(event) => onKeyDown(index, event)}
                onFocus={(event) => event.target.select()}
                aria-label={`Access code character ${index + 1}`}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? 'access-code-error' : undefined}
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                autoFocus={index === 0}
                inputMode="text"
                maxLength={CODE_LENGTH}
                disabled={pending}
                className="h-14 w-12 sm:h-16 sm:w-14 rounded-xl border border-edge bg-canvas text-center text-xl text-ink outline-none focus:border-primary focus:ring-2 focus:ring-ring/40 disabled:opacity-50"
              />
            ))}
          </div>
        </fieldset>
        {error && <p id="access-code-error" role="alert" className="mt-3 text-sm text-danger">{error}</p>}
        <p role="status" aria-live="polite" className="mt-3 text-sm text-ink-muted">
          {pending ? 'Checking…' : 'Your code will be checked automatically.'}
        </p>
      </form>
    </div>
  );
}
